import React, { createContext, ReactNode, useCallback, useState } from 'react'

interface SchedulerStepProviderProps {
  children: ReactNode
}

export interface DependenteInfo {
  id: number
  cpf: string
  nome_completo: string
  email: string
  data_nascimento: string
  pcd: boolean
  pessoa_dependente: {
    parentesco: string
  }[]
}

interface SchedulerStepContextType {
  currentStep: number
  cnpjNumber: string | null
  companyCountry: string | null
  companyName: string | null
  companyAddress: string | null
  userProfile: 'Cidadão' | 'Empresa' | 'Servidor' | null
  serviceType: 'presencial' | 'online' | null
  isVisibleButton: boolean
  onlyOnePublic: boolean
  selectedDependente: DependenteInfo | null
  isSchedulingForDependent: boolean
  handleSetOnlyOnePublic: (value: boolean) => void
  onChangeCountry: (value: string) => void
  onChangeCNPJ: (value: React.ChangeEvent<HTMLInputElement>) => void
  onChangeCompanyName: (value: string | null) => void
  onChangeCompanyAddress: (value: string | null) => void
  handleChangeStep: (value: 'next' | 'prev') => void
  handleChangeProfile: (value: 'Cidadão' | 'Empresa' | 'Servidor') => void
  handleChangeSetServiceType: (value: 'online' | 'presencial') => void
  handleSelectDependente: (dependente: DependenteInfo | null) => void
  handleSetIsSchedulingForDependent: (value: boolean) => void
  handleResetContext: () => void
  handleResetProfileData: () => void
}

export const SchedulerStepContext = createContext(
  {} as SchedulerStepContextType,
)

export function SchedulerStepProvider({
  children,
}: SchedulerStepProviderProps): JSX.Element {
  const [onlyOnePublic, setOnlyOnePublic] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [cnpjNumber, setCnpjNumber] = useState<string | null>(null)
  const [companyCountry, setCompanyCountry] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState<string | null>(null)
  const [companyAddress, setCompanyAddress] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<
    'Cidadão' | 'Empresa' | 'Servidor' | null
  >(null)
  const [serviceType, setServiceType] = useState<
    'online' | 'presencial' | null
  >()
  const [selectedDependente, setSelectedDependente] =
    useState<DependenteInfo | null>(null)
  const [isSchedulingForDependent, setIsSchedulingForDependent] =
    useState<boolean>(false)

  const handleStep = (type: 'next' | 'prev') => {
    setCurrentStep((oldState) => {
      if (type === 'next') {
        return oldState + 1
      }
      return oldState - 1
    })
  }

  const handleChangeCountry = useCallback((value: string) => {
    setCompanyCountry(value)
  }, [])

  const handleChangeCompanyName = useCallback((value: string | null) => {
    setCompanyName(value)
  }, [])

  const handleChangeCompanyAddress = useCallback((value: string | null) => {
    setCompanyAddress(value)
  }, [])

  const handleSetCNPJ = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.length <= 18) {
        setCnpjNumber(event.target.value)
      }
    },
    [],
  )

  const handleChangeProfile = (current: 'Cidadão' | 'Empresa' | 'Servidor') => {
    setUserProfile(current)
  }

  const handleChangeSetServiceType = (
    value: 'presencial' | 'online' | null,
  ) => {
    setServiceType(value)
    handleStep('next')
  }

  const isVisibleButton =
    userProfile === 'Cidadão' ||
    userProfile === 'Servidor' ||
    (userProfile === 'Empresa' &&
      typeof companyCountry === 'string' &&
      companyCountry === 'SALVADOR')

  const handleSelectDependente = useCallback(
    (dependente: DependenteInfo | null) => {
      setSelectedDependente(dependente)
      setIsSchedulingForDependent(!!dependente)
    },
    [],
  )

  const handleResetContext = useCallback(() => {
    setCurrentStep(0)
    setCnpjNumber(null)
    setCompanyCountry(null)
    setCompanyName(null)
    setCompanyAddress(null)
    setUserProfile(null)
    setServiceType(null)
    setSelectedDependente(null)
    setIsSchedulingForDependent(false)
  }, [])

  const handleResetProfileData = useCallback(() => {
    // Limpa apenas os dados relacionados ao perfil selecionado
    setCnpjNumber(null)
    setCompanyCountry(null)
    setCompanyName(null)
    setCompanyAddress(null)
    setSelectedDependente(null)
    setIsSchedulingForDependent(false)
    // NÃO limpa userProfile para permitir re-seleção
  }, [])

  return (
    <SchedulerStepContext.Provider
      value={{
        cnpjNumber,
        companyCountry,
        companyName,
        companyAddress,
        currentStep,
        userProfile,
        serviceType,
        isVisibleButton,
        onlyOnePublic,
        selectedDependente,
        isSchedulingForDependent,
        handleSetOnlyOnePublic: setOnlyOnePublic,
        handleChangeStep: handleStep,
        onChangeCNPJ: handleSetCNPJ,
        onChangeCountry: handleChangeCountry,
        onChangeCompanyName: handleChangeCompanyName,
        onChangeCompanyAddress: handleChangeCompanyAddress,
        handleChangeProfile,
        handleChangeSetServiceType,
        handleSelectDependente,
        handleSetIsSchedulingForDependent: setIsSchedulingForDependent,
        handleResetContext,
        handleResetProfileData,
      }}
    >
      {children}
    </SchedulerStepContext.Provider>
  )
}
